import React, { useId } from 'react';
import { Search, X, Plus, Minus } from 'lucide-react';

export interface FieldProps {
  label?: React.ReactNode;
  hint?: string;
  error?: string;
  required?: boolean;
  id?: string;
  children: React.ReactNode;
  className?: string;
}

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  sizeVariant?: 'sm' | 'md' | 'lg';
  isLatin?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ error, sizeVariant = 'md', isLatin = false, className = '', type, dir, ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-8 px-2.5 text-xs',
      md: 'h-12 lg:h-10 min-h-[48px] lg:min-h-[40px] px-3 text-sm',
      lg: 'h-12 lg:h-11 min-h-[48px] lg:min-h-[44px] px-3.5 lg:px-3 text-base lg:text-[15px]'
    };

    const hasOwnAlign = /(^|\s)text-(left|right|center)(\s|$)/.test(className);
    const isLatinInput =
      isLatin || type === 'email' || type === 'password' || type === 'url' || dir === 'ltr';

    return (
      <input
        ref={ref}
        type={type}
        dir={dir ?? (isLatinInput ? 'ltr' : undefined)}
        className={`w-full rounded-xl bg-base-100 border text-base-content placeholder:text-base-content/40 transition focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:bg-base-200 disabled:cursor-not-allowed ${
          sizeClasses[sizeVariant]
        } ${
          `${isLatin ? 'font-mono' : ''} ${hasOwnAlign ? '' : 'text-start'}`
        } ${
          error
            ? 'border-error ring-1 ring-error focus-visible:ring-error'
            : 'border-base-300 focus-visible:border-primary focus-visible:ring-primary/30'
        } ${className}`}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export interface SearchInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  onClear?: () => void;
  sizeVariant?: 'sm' | 'md' | 'lg';
  isLatin?: boolean;
}

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onClear,
      sizeVariant = 'md',
      isLatin = false,
      className = '',
      dir,
      ...props
    },
    ref
  ) => {
    const hasValue = Boolean(value);

    const sizeClasses = {
      sm: 'h-8 text-xs',
      md: 'h-12 lg:h-10 min-h-[48px] lg:min-h-[40px] text-sm',
      lg: 'h-12 text-base'
    };

    return (
      <div className={`relative flex items-center ${className}`}>
        <Search className="absolute start-3 size-4 text-base-content/40 pointer-events-none" />
        <input
          ref={ref}
          type="search"
          value={value}
          dir={dir ?? (isLatin ? 'ltr' : undefined)}
          className={`w-full ps-9 pe-9 rounded-xl bg-base-100 border border-base-300 text-base-content placeholder:text-base-content/40 transition focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/30 ${
            sizeClasses[sizeVariant]
          } ${isLatin ? 'font-mono' : ''}`}
          {...props}
        />
        {hasValue && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="پاک کردن جستجو"
            className="absolute end-2.5 p-1 text-base-content/40 hover:text-base-content transition rounded-lg"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    );
  }
);
SearchInput.displayName = 'SearchInput';

export interface StepperProps {
  value: number;
  onIncrement: () => void;
  onDecrement: () => void;
  label?: string;
  unit?: string;
  min?: number;
  max?: number;
  className?: string;
}

export const Stepper: React.FC<StepperProps> = ({
  value,
  onIncrement,
  onDecrement,
  label,
  unit,
  min,
  max,
  className = ''
}) => {
  return (
    <div
      className={`inline-flex items-center justify-between border border-base-300 bg-base-100 rounded-xl p-1 h-12 max-md:h-[52px] lg:h-10 min-h-[48px] max-md:min-h-[52px] lg:min-h-[40px] ${className}`}
    >
      <button
        type="button"
        onClick={onDecrement}
        disabled={min !== undefined && value <= min}
        aria-label="کاهش"
        className="size-8 max-md:size-11 inline-flex items-center justify-center rounded-lg hover:bg-base-200 active:scale-95 text-base-content/80 disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <Minus className="size-4" />
      </button>
      <div className="px-3 flex items-center gap-1 font-mono text-sm font-semibold" dir="ltr">
        <span className="tabular-nums">{value}</span>
        {unit && <span className="text-xs text-base-content/60 font-sans">{unit}</span>}
        {label && <span className="text-xs text-base-content/60 font-sans">{label}</span>}
      </div>
      <button
        type="button"
        onClick={onIncrement}
        disabled={max !== undefined && value >= max}
        aria-label="افزایش"
        className="size-8 max-md:size-11 inline-flex items-center justify-center rounded-lg hover:bg-base-200 active:scale-95 text-base-content/80 disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <Plus className="size-4" />
      </button>
    </div>
  );
};
Stepper.displayName = 'Stepper';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  sizeVariant?: 'sm' | 'md' | 'lg';
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ error, sizeVariant = 'md', className = '', children, ...props }, ref) => {
    const sizeClasses = {
      sm: 'h-8 px-2.5 text-xs',
      md: 'h-12 lg:h-10 min-h-[48px] lg:min-h-[40px] px-3 text-sm',
      lg: 'h-12 px-4 text-base'
    };

    return (
      <select
        ref={ref}
        className={`w-full rounded-xl bg-base-100 border text-base-content transition focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed ${
          sizeClasses[sizeVariant]
        } ${
          error
            ? 'border-error ring-1 ring-error focus-visible:ring-error'
            : 'border-base-300 focus-visible:border-primary focus-visible:ring-primary/30'
        } ${className}`}
        {...props}
      >
        {children}
      </select>
    );
  }
);
Select.displayName = 'Select';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
  isLatin?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ error, isLatin = false, className = '', dir, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        dir={dir ?? (isLatin ? 'ltr' : undefined)}
        className={`w-full p-3 rounded-xl bg-base-100 border text-sm text-base-content placeholder:text-base-content/40 transition focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40 disabled:cursor-not-allowed ${
          isLatin ? 'font-mono' : ''
        } ${
          error
            ? 'border-error ring-1 ring-error focus-visible:ring-error'
            : 'border-base-300 focus-visible:border-primary focus-visible:ring-primary/30'
        } ${className}`}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

interface FieldComponentType extends React.FC<FieldProps> {
  Input: typeof Input;
  SearchInput: typeof SearchInput;
  Stepper: typeof Stepper;
  Select: typeof Select;
  Textarea: typeof Textarea;
}

export const Field: FieldComponentType = ({
  label,
  hint,
  error,
  required,
  id,
  children,
  className = ''
}) => {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const messageId = `${fieldId}-msg`;
  const hasMessage = Boolean(error || hint);

  const renderChild = () => {
    if (!React.isValidElement(children)) return children;
    const child = children as React.ReactElement<{
      id?: string;
      'aria-invalid'?: React.AriaAttributes['aria-invalid'];
      'aria-describedby'?: string;
    }>;
    const extra: { id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string } = {};
    if (!child.props.id) extra.id = fieldId;
    if (error && child.props['aria-invalid'] === undefined) extra['aria-invalid'] = true;
    if (hasMessage) {
      extra['aria-describedby'] = [child.props['aria-describedby'], messageId].filter(Boolean).join(' ');
    }
    return Object.keys(extra).length > 0 ? React.cloneElement(child, extra) : children;
  };

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {label && (
        <label
          htmlFor={fieldId}
          className="text-[13px] font-medium text-base-content/80 flex items-center justify-between"
        >
          <span>
            {label}
            {required && <span className="text-error ms-1">*</span>}
          </span>
        </label>
      )}
      {renderChild()}
      {error ? (
        <span id={messageId} role="alert" className="text-xs text-error-soft">
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="text-xs text-base-content/60">{hint}</span>
      ) : null}
    </div>
  );
};

Field.displayName = 'Field';
Field.Input = Input;
Field.SearchInput = SearchInput;
Field.Stepper = Stepper;
Field.Select = Select;
Field.Textarea = Textarea;
